import logo from "@/assets/logo.png";
import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { UserGroups, UserProfile, UserType } from "@/types/user";
import {
    Baby,
    BadgeEuro,
    BarChart3,
    Building2,
    CheckCheck,
    Cross,
    ExternalLink,
    FileUser,
    GraduationCap,
    KeyRound,
    LibraryBig,
    Mailbox,
    Megaphone,
    MapPinned,
    Merge,
    PhoneCall,
    PhoneForwarded,
    PhoneOff,
    Puzzle,
    ReceiptEuro,
    Recycle,
    ScrollText,
    ShoppingCart,
    Zap
} from "lucide-react";
import React, { ReactElement } from "react";
import { NavLink } from "react-router-dom";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "./ui/navigation-menu";

interface MainMenuProps extends React.HTMLAttributes<HTMLDivElement> {}

export function MainMenu({ className }: MainMenuProps) {
    const { user, has_any_group, has_any_profile } = useUser();
    // Top-level "Inscriptions" is only for true external providers.
    // Internal users (system_admin, registration/finance) keep Tools > Registrations.
    const show_external_registrations_menu =
        (!!user?.external_provider || (user?.external_providers?.length ?? 0) > 0) &&
        !has_any_profile(["system_admin"]) &&
        !has_any_group(["app_registration_admin", "team_finance"]);

    const catalogs: { title: string; href: string; description: string; icon: ReactElement }[] = [
        {
            title: "Santé",
            href: "/catalogue/sante",
            description: "Tout le catalogue santé",
            icon: <Cross size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Généraliste",
            href: "/catalogue/generaliste",
            description: "Tout le catalogue généraliste",
            icon: <LibraryBig size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Alternance",
            href: "/catalogue/alternance",
            description: "Tout le catalogue Alternance",
            icon: <GraduationCap size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Edumiam",
            href: "/catalogue/edumiam",
            description: "Tout le catalogue Edumiam",
            icon: <Baby size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />
        },
    ];

    const apprenticeships: { title: string; href: string; description: string; icon: ReactElement; groups: UserGroups[] }[] = [
        {
            title: "Candidats",
            href: "/alternance/candidats",
            description: "Gestion des candidats",
            icon: <FileUser size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "La carte",
            href: "/alternance/carte",
            description: "Accéder à la carte",
            icon: <MapPinned size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "Mes matchs",
            href: "/alternance/matchs",
            description: "Accéder aux matchs offres/candidats",
            icon: <Puzzle size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "Étudiants",
            href: "/alternance/etudiants",
            description: "Gestion de nos étudiants",
            icon: <GraduationCap size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_apprenticeship_admin", "team_apprenticeship_csm", "team_support"]
        }
    ];

    const registrations: { title: string; href: string; description: string; icon: ReactElement; groups: UserGroups[] }[] = [
        {
            title: "Formulaires de ventes",
            href: "/inscriptions/ventes",
            description: "Gestion des formulaires de ventes",
            icon: <BadgeEuro size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_registration_admin"]
        },
        {
            title: "Inscriptions autonomes",
            href: "/inscriptions/autonomes",
            description: "Gestion des inscriptions autonomes",
            icon: <ShoppingCart size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_registration_admin"]
        },
        {
            title: "Inscriptions externes",
            href: "/inscriptions/externes",
            description: "Suivi des apporteurs d'affaire",
            icon: <ExternalLink size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_registration_admin", "team_finance", "team_external_provider"]
        }
    ];

    // `profiles` and `allowed` are optional because the tools menu is mid-migration: most
    // entries are still reached by group, some by profile, the dialer by a right the server
    // computes from the profile matrix, and an entry shows as soon as one of them grants.
    const tools: { title: string; href: string; description: string; icon: ReactElement; groups: UserGroups[]; profiles?: UserProfile[]; allowed?: (user: UserType) => boolean }[] = [
        {
            title: "Fusion",
            href: "/outils/fusion",
            description: "Fusionner deux comptes",
            icon: <Merge size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_csm", "team_adv"]
        },
        {
            title: "Bloctel",
            href: "/outils/bloctel",
            description: "Passer un contact en Bloctel",
            icon: <PhoneOff size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_csm", "team_adv"]
        },
        {
            title: "Correction de facture",
            href: "/outils/correction-facture",
            description: "Corriger le client sur une facture",
            icon: <ReceiptEuro size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_finance"]
        },
        {
            title: "Validation des ventes",
            href: "/outils/ventes",
            description: "Valider une vente à partir d'une Task",
            icon: <CheckCheck size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin"]
        },
        {
            title: "Appels",
            href: "/outils/appels?o=-started_at",
            description: "Rechercher et accéder aux appels",
            icon: <PhoneCall size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_adv", "team_support", "app_voice_admin"]
        },
        {
            title: "Dialer campaigns",
            // The generic campaign list, served by `sf_like` from `CampaignConfig`. The
            // workflow and card screens are reached from there, which is why this entry
            // no longer points at the bespoke `/outils/campagnes` index.
            href: "/dialer-campaigns",
            description: "Dialer engine: campaigns, workflows and cards",
            icon: <PhoneForwarded size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            allowed: user => Boolean(user.can_open_dialer)
        },
        {
            title: "Campagne postale",
            href: "/outils/campagnes-postales",
            description: "Créer une campagne marketing postale",
            icon: <Mailbox size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "app_tools_mailing_campaign"]
        },
        {
            icon: <Zap size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Injection flash",
            href: "/outils/injection-flash",
            description: "Créer un batch d'injection flash",
            groups: ["app_tools_admin", "app_tools_flash_injection"]
        },
        {
            icon: <Building2 size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Recherche SIRET",
            href: "/outils/siret",
            description: "Rechercher un SIRET",
            groups: ["app_tools_admin"]
        },
        {
            icon: <BarChart3 size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Reports",
            href: "/outils/rapports",
            description: "Metabase reports and dashboards",
            groups: []
        },
        {
            icon: <KeyRound size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Coffre",
            href: "/outils/coffre",
            description: "Identifiants partagés OPCO et plateformes",
            groups: []
        },
        {
            title: "Lead recycling",
            href: "/outils/recyclage-leads",
            description: "Search Diabolocom contacts and launch a recycling wave",
            icon: <Recycle size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            profiles: ["sales_admin", "system_admin"]
        },
        {
            title: "Offres WeRecruit",
            // The generic list served by `sf_like` from `WeRecruitOfferConfig`; the boards and
            // their caps are reached from `/werecruit-job-boards`.
            href: "/werecruit-offers",
            description: "Offres publiées sur WeRecruit et sites de diffusion",
            icon: <Megaphone size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            profiles: ["sales_admin", "system_admin"]
        },
        {
            title: "Journal WeRecruit",
            href: "/werecruit-events",
            description: "Ce que les lambdas WeRecruit ont publié, retiré, ignoré, et pourquoi",
            icon: <ScrollText size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            profiles: ["sales_admin", "system_admin"]
        },
        {
            title: "Sites WeRecruit",
            href: "/werecruit-job-boards",
            description: "Sites de diffusion WeRecruit, défauts et plafonds de publication",
            icon: <Megaphone size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            profiles: ["sales_admin", "system_admin"]
        }
    ];

    const billing: { title: string; href: string; description: string; icon: ReactElement; groups: UserGroups[] }[] = [
        {
            title: "Facturation DPC",
            href: "/facturation/andpc",
            description: "Gestion des factures DPC",
            icon: <BadgeEuro size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_billing_admin", "team_finance", "team_support"]
        },
        {
            title: "Facturation FIFPL",
            href: "/facturation/fifpl",
            description: "Gestion des factures FIFPL",
            icon: <BadgeEuro size={16} className="mb-1 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_billing_admin", "team_finance", "team_support"]
        }
    ];

    return (
        <NavigationMenu className={cn(className)}>
            <NavigationMenuList>
                <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                        <span>
                            <NavLink
                                to="/"
                                className="group inline-flex h-9 w-max items-center justify-center rounded-md  px-4 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50"
                            >
                                <img src={logo} alt="Walter CRM" className="h-4" />
                            </NavLink>
                        </span>
                    </NavigationMenuLink>
                </NavigationMenuItem>
                {show_external_registrations_menu && (
                    <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                            <span>
                                <NavLink
                                    to="/inscriptions/externes"
                                    className={({ isActive }) => {
                                        return cn(
                                            "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50",
                                            isActive ? "bg-accent/75" : ""
                                        );
                                    }}
                                >
                                    Inscriptions
                                </NavLink>
                            </span>
                        </NavigationMenuLink>
                    </NavigationMenuItem>
                )}
                {has_any_group(["app_medrep_admin", "team_medrep"]) && !has_any_group(["app_registration_admin"]) && (
                    <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                            <span>
                                <NavLink
                                    to="/dm/"
                                    className={({ isActive }) => {
                                        return cn(
                                            "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50",
                                            isActive ? "bg-accent/75" : ""
                                        );
                                    }}
                                >
                                    Contacts
                                </NavLink>
                            </span>
                        </NavigationMenuLink>
                    </NavigationMenuItem>
                )}
                {/* Hide this section to non manager and to those who can access Registration section */}
                {!has_any_group(registrations.reduce((a, component) => a.concat(component.groups), [] as UserGroups[])) && user?.is_manager && (
                    <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                            <span>
                                <NavLink
                                    to="/inscriptions/ventes"
                                    className={({ isActive }) => {
                                        return cn(
                                            "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50",
                                            isActive ? "bg-accent/75" : ""
                                        );
                                    }}
                                >
                                    Formulaires de vente
                                </NavLink>
                            </span>
                        </NavigationMenuLink>
                    </NavigationMenuItem>
                )}
                <NavigationMenuItem>
                    <NavigationMenuTrigger>Catalogue</NavigationMenuTrigger>
                    <NavigationMenuContent>
                        <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] ">
                            {catalogs.map(component => (
                                <ListItem key={component.title} title={component.title} to={component.href} icon={component.icon}>
                                    {component.description}
                                </ListItem>
                            ))}
                        </ul>
                    </NavigationMenuContent>
                </NavigationMenuItem>

                {has_any_group(apprenticeships.reduce((a, component) => a.concat(component.groups), [] as UserGroups[])) && (
                    <NavigationMenuItem>
                        <NavigationMenuTrigger>Alternances</NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] ">
                                {apprenticeships
                                    .filter(component => has_any_group(component.groups))
                                    .map(component => (
                                        <ListItem key={component.title} title={component.title} to={component.href} icon={component.icon}>
                                            {component.description}
                                        </ListItem>
                                    ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                )}

                {has_any_group(registrations.reduce((a, component) => a.concat(component.groups), [] as UserGroups[])) && (
                    <NavigationMenuItem>
                        <NavigationMenuTrigger>Inscriptions</NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] ">
                                {registrations
                                    .filter(component => has_any_group(component.groups))
                                    .map(component => (
                                        <ListItem key={component.title} title={component.title} to={component.href} icon={component.icon}>
                                            {component.description}
                                        </ListItem>
                                    ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                )}
                <NavigationMenuItem>
                        <NavigationMenuTrigger>Outils</NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[650px] md:grid-cols-2 lg:w-[750px] lg:grid-cols-3 ">
                                {tools
                                    .filter(component => {
                                        // `has_any_group([])` and `has_any_profile([])` both answer
                                        // yes, so the two dimensions are tested only when the entry
                                        // actually declares them. An entry declaring neither stays
                                        // visible to everyone, as it was.
                                        if (component.groups.length === 0 && !component.profiles?.length && !component.allowed) return true;
                                        return (
                                            (component.groups.length > 0 && has_any_group(component.groups)) ||
                                            (!!component.profiles?.length && has_any_profile(component.profiles)) ||
                                            (!!component.allowed && !!user && component.allowed(user))
                                        );
                                    })
                                    .map(component => (
                                        <ListItem key={component.title} title={component.title} to={component.href} icon={component.icon}>
                                            {component.description}
                                        </ListItem>
                                    ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                {has_any_group(["app_billing_admin", "team_finance", "team_support"]) && (
                    <NavigationMenuItem>
                        <NavigationMenuTrigger>Facturation</NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[650px] md:grid-cols-2 lg:w-[750px] lg:grid-cols-3 ">
                                {billing
                                    .filter(component => has_any_group(component.groups))
                                    .map(component => (
                                        <ListItem key={component.title} title={component.title} to={component.href} icon={component.icon}>
                                            {component.description}
                                        </ListItem>
                                    ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                )}
                {has_any_group(["app_medrep_admin", "team_medrep"]) && has_any_group(["app_registration_admin"]) && (
                    <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                            <span>
                                <NavLink
                                    to="/dm/"
                                    className={({ isActive }) => {
                                        return cn(
                                            "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50",
                                            isActive ? "bg-accent/75" : ""
                                        );
                                    }}
                                >
                                    Délégués médicaux
                                </NavLink>
                            </span>
                        </NavigationMenuLink>
                    </NavigationMenuItem>
                )}
            </NavigationMenuList>
        </NavigationMenu>
    );
}

const ListItem = React.forwardRef<
    React.ElementRef<"a">,
    React.ComponentPropsWithoutRef<"a"> & {
        title: string;
        icon?: React.ReactNode;
        to: string;
    }
>(({ className, title, children, icon, to, ...props }, ref) => {
    return (
        <li>
            <NavigationMenuLink asChild>
                <NavLink
                    ref={ref}
                    to={to!}
                    className={cn(
                        "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                        className
                    )}
                    {...props}
                >
                    <div className="text-sm font-medium leading-none">
                        {icon}
                        {title}
                    </div>
                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
                </NavLink>
            </NavigationMenuLink>
        </li>
    );
});
ListItem.displayName = "ListItem";
